import { Box, Button, Input, Switch, Text, VStack, Flex, Heading } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { FormField } from '../../../../components/FormField'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { SectionCard } from '../../../../components/SectionCard'
import { useHasRole, useAppToast } from '../../../../helpers'
import { ISidebarBannerData, ISidebarBannerRequest } from '../../../../interfaces/Website/SidebarBanner/ISidebarBanner'
import SidebarBannerService from '../../../../services/Website/SidebarBannerService'

const Error = styled(Text)`
  color: red.500;
  font-size: 0.8rem;
`

export const SidebarBannerPage = () => {
  const toast = useAppToast()
  const service = new SidebarBannerService()
  const hasRole = useHasRole()
  const permissions = {
    editBanner: hasRole('edit_banner')
  }

  const [data, setData] = useState<ISidebarBannerData>({
    image_path: '',
    link_url: '',
    is_active: false
  })

  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('')
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await service.get()
      setData(response)
      setImagePreviewUrl(response.image_base64 || '')
    } catch (error) {
      toast.error('Erro ao carregar dados', 'Erro ao carregar informações do banner lateral.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    try {
      setSaving(true)
      setErrors({})

      const formData: ISidebarBannerRequest = {
        link_url: data.link_url,
        is_active: data.is_active
      }

      if (selectedImage) {
        formData.image = selectedImage
      }

      await service.update(formData)

      toast.success('Banner atualizado', 'Banner lateral atualizado com sucesso.')

      // Recarregar os dados após salvar
      await fetchData()
      setSelectedImage(null)
    } catch (err) {
      const error = err as AxiosError<{ errors: Record<string, string[]> }>
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors)
        toast.error('Erro de validação', 'Insira os parâmetros corretamente.')
      } else {
        toast.error('Erro inesperado', 'Ocorreu um erro ao atualizar o banner.')
      }
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h2">Banner Lateral</Heading>
      </Flex>

      <SectionCard maxW="600px">
        <VStack spacing={6} align="stretch">
          {/* Upload de Imagem */}
          <Box>
            <ImageCropInput
              label="Imagem do Banner"
              value={selectedImage ?? imagePreviewUrl ?? null}
              onChange={(file) => setSelectedImage(file)}
              aspectRatio={210 / 650}
              suggestedSize="210px x 650px"
              previewHeight="200px"
              isDisabled={!permissions.editBanner || saving}
              error={errors?.image}
            />
          </Box>

          {/* Link URL */}
          <Box>
            <FormField label="Link do Banner">
              <Input
                type="url"
                placeholder="https://exemplo.com"
                value={data.link_url}
                onChange={(e) => setData({ ...data, link_url: e.target.value })}
                size="md"
                bg="whiteAlpha"
                borderWidth="1px"
                borderColor="gray.300"
                borderRadius="md"
                disabled={!permissions.editBanner}
              />
            </FormField>
            {errors?.link_url && <Error color="red.500">{errors.link_url[0]}</Error>}
          </Box>

          {/* Status Ativo/Inativo */}
          <Box>
            <FormField label="Status">
              <Switch
                isChecked={data.is_active}
                onChange={(e) => setData({ ...data, is_active: e.target.checked })}
                isDisabled={!permissions.editBanner}
              />
            </FormField>
            <Text variant="caption" mt={1}>
              {data.is_active ? 'Banner ativo no site' : 'Banner inativo no site'}
            </Text>
          </Box>

          {/* Botão Salvar */}
          <Box pt={4}>
            {permissions.editBanner && (
              <Button
                colorScheme="magenta"
                size="md"
                width="full"
                onClick={handleSubmit}
                isLoading={saving}
                loadingText="Salvando..."
              >
                Salvar Banner
              </Button>
            )}
          </Box>
        </VStack>
      </SectionCard>
    </>
  )
}
